import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { RevealLines, Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import RelicCard from "@/components/RelicCard";
import { useProducts } from "@/hooks/useProducts";
import { useCart } from "@/context/CartContext";

const GHOST = "/products/the-ghost-in-your-bed.jpg";

const eur = (v) => `€${v.toLocaleString("en-US")}`;

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const { products } = useProducts();
  const { addItem } = useCart();
  const xoxo = products.find((p) => p.slug === "xoxo");

  return (
    <div data-testid="home-page">
      {/* A. HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] overflow-hidden grain flex flex-col">
        <motion.div style={{ y, scale }} className="absolute inset-0">
          <img
            src={GHOST}
            alt="Beautifully Disturbed"
            className="w-full h-full object-cover"
            style={{ filter: "brightness(0.62) saturate(0.9) contrast(1.05)" }}
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/55 to-ink" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_40%,_transparent_15%,_rgba(7,7,7,0.75)_90%)]" />

        <motion.div style={{ opacity: heroOpacity }} className="relative z-10 flex-1 container-lux flex flex-col justify-center items-center text-center pt-28 pb-16">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1] }}
            className="font-display uppercase text-ivory leading-[0.82] tracking-[0.04em] text-6xl sm:text-7xl lg:text-[8.5rem] mb-10"
          >
            <span className="block">Beautifully</span>
            <span className="block">Disturbed</span>
          </motion.h1>
          <h2 className="heading-serif text-2xl sm:text-3xl lg:text-4xl leading-[1.05] max-w-3xl text-ivory/90">
            <RevealLines lines={["Childhood nightmares. Beautifully bottled."]} delay={0.6} stagger={0.14} />
          </h2>
          <Reveal delay={1.2} className="mt-8 max-w-xl">
            <p className="text-base md:text-lg text-ivory/75 font-sans leading-relaxed">
              Dark nostalgia, unsettling memory and the beauty hidden inside fear,
              distilled into collectible extraits by an independent niche house.
            </p>
          </Reveal>
          <Reveal delay={1.45} className="mt-11 flex flex-wrap gap-4 justify-center">
            <Link
              to="/collection"
              data-testid="discover-collection-btn"
              className="btn-relic border-ivory bg-ivory text-ink hover:bg-blood hover:border-blood hover:text-ivory"
            >
              Explore the Collection
            </Link>
            <Link
              to="/story"
              data-testid="explore-world-btn"
              className="btn-relic border-ivory/40 text-ivory hover:border-blood hover:text-blood"
            >
              Discover Our World
            </Link>
          </Reveal>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8, duration: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 label-mono text-smoke"
        >
          Scroll into the dark
        </motion.div>
      </section>

      {/* MARQUEE */}
      <section className="py-9 border-y border-smoke/15 bg-ink">
        <Marquee items={["The Ghost in Your Bed", "Matryoshka Skin", "XOXO"]} />
      </section>

      {/* B. COLLECTION GRID */}
      <section className="container-lux py-24 lg:py-36" data-testid="collection-showcase">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <p className="label-mono mb-5 text-blood">The Collection</p>
            <h2 className="heading-serif text-4xl md:text-6xl max-w-2xl leading-[1.02]">
              Beautiful fears, beautifully bottled.
            </h2>
          </div>
          <Link
            to="/collection"
            className="label-mono text-blood hover:text-ivory transition-colors whitespace-nowrap"
            data-testid="view-all-relics"
          >
            View all →
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
          {products.map((p, i) => (
            <RelicCard key={p.slug} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* C. BRAND STORY */}
      <section className="py-24 lg:py-36 border-y border-smoke/12" data-testid="brand-story">
        <div className="container-lux grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <p className="label-mono mb-5 text-blood">The Concept</p>
            <h2 className="heading-serif text-4xl md:text-5xl leading-[1.05]">
              The things that frightened us were the most beautiful
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-6 lg:col-start-7 space-y-6 text-base md:text-lg text-ivory/75 font-sans leading-relaxed">
            <p>
              Every child keeps a private museum of dread, the shape in the wardrobe, the doll that
              watched too closely, the fairy who came for something precious in the night.
            </p>
            <p>
              BEAUTIFULLY DISTURBED reopens that museum and refuses to look away. We take the memories
              that unsettle and recast them as objects of desire: sculptural, strange, and undeniably
              luxurious. Perfume as confrontation. Beauty as the thing you were afraid of all along.
            </p>
          </Reveal>
        </div>
      </section>

      {/* D. XOXO SIGNATURE FEATURE */}
      <section className="relative py-28 lg:py-44 overflow-hidden" data-testid="xoxo-feature">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
          <span className="font-display text-[34vw] leading-none text-ivory/[0.04] tracking-tight">XOXO</span>
        </div>
        <div className="container-lux relative z-10 max-w-2xl">
          <p className="label-mono mb-6 text-blood">The House Icon</p>
          <h2 className="heading-serif text-5xl md:text-7xl leading-[0.95] mb-8">
            XO<span className="text-blood">XO</span>
          </h2>
          <div className="crimson-rule w-40 mb-8" />
          <p className="text-base md:text-lg text-ivory/80 font-sans leading-relaxed mb-10">
            A face split between ivory calm and black abyss, cracked open by a single seam of crimson.
            XOXO is the signature of the house, tender obsession bottled, equal parts caress and wound.
            The signature of the house; the one that looks back.
          </p>
          <div className="flex flex-wrap items-center gap-6">
            <Link
              to="/product/xoxo"
              data-testid="xoxo-discover-btn"
              className="btn-relic border-ivory bg-ivory text-ink hover:bg-blood hover:border-blood hover:text-ivory"
            >
              Discover XOXO
            </Link>
            {xoxo && (
              <button
                onClick={() => addItem(xoxo)}
                data-testid="xoxo-add-btn"
                className="btn-relic border-ivory/40 text-ivory hover:border-blood hover:text-blood"
              >
                Add to Bag · {eur(xoxo.price)}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* E. COMPLETE COLLECTION (editorial) */}
      <section className="bg-onyx py-24 lg:py-36 border-y border-smoke/12" data-testid="complete-collection">
        <div className="container-lux">
          <p className="label-mono mb-10 text-blood text-center">The Complete Collection</p>
          <div className="divide-y divide-smoke/12 border-y border-smoke/12">
            {products.map((p, i) => (
              <Link
                key={p.slug}
                to={`/product/${p.slug}`}
                data-testid={`collection-row-${p.slug}`}
                className="group grid grid-cols-12 items-center gap-4 py-7 md:py-9"
              >
                <span className="col-span-2 md:col-span-1 font-mono text-xs text-smoke">0{i + 1}</span>
                <span className="col-span-7 md:col-span-7 heading-serif text-2xl md:text-4xl group-hover:text-blood transition-colors">
                  {p.name}
                </span>
                <span className="hidden md:block md:col-span-3 text-sm text-smoke font-sans">{p.tagline}</span>
                <span className="col-span-3 md:col-span-1 text-right font-mono text-sm">{eur(p.price)}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* F. PHILOSOPHY */}
      <section className="relative py-28 lg:py-48 overflow-hidden grain" data-testid="philosophy">
        {/* Atmospheric cracked-face background fading into shadow */}
        <div className="absolute inset-0 pointer-events-none select-none" aria-hidden="true">
          <img
            src="/brand/cracked-face.jpg"
            alt=""
            className="w-full h-full object-cover opacity-[0.28]"
            style={{ filter: "brightness(0.7) saturate(0.85) contrast(1.05)" }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/55 to-ink" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_50%,_transparent_8%,_rgba(7,7,7,0.92)_82%)]" />
        </div>

        <div className="container-lux relative z-10 max-w-3xl text-center">
          <p className="label-mono mb-10 text-blood">The Philosophy</p>
          <h2 className="font-display uppercase text-ivory leading-[0.95] tracking-[0.02em] text-4xl sm:text-5xl lg:text-6xl text-balance">
            Beauty was never meant to be comfortable.
          </h2>
          <div className="crimson-rule w-40 mx-auto mt-12 mb-12" />
          <p className="text-base md:text-lg text-ivory/75 font-sans leading-relaxed max-w-xl mx-auto">
            We do not make perfume to please. We make it to be remembered, the scent
            that unsettles before it seduces, that lingers like a memory you were never
            meant to keep.
          </p>
          <p className="mt-6 text-base text-ivory/60 font-sans leading-relaxed max-w-xl mx-auto">
            Artistic perfumery for those who find beauty in the uncanny. Individual,
            uncompromising, and made in small numbers for collectors of the extraordinary.
          </p>
        </div>
      </section>
    </div>
  );
}
